// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import 'react-datepicker/dist/react-datepicker.css';
import clsx from 'clsx';
import {
    MessageBar,
    MessageBarType,
    PrimaryButton,
    Spinner,
    SpinnerSize,
    TextField
} from 'office-ui-fabric-react';
import DatePicker from 'react-datepicker';
import * as KeyCode from 'keycode-js';

import { Icon } from 'office-ui-fabric-react/lib/Icon';

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import Collapsible from './../../components/Collapsible';
import PackageInstructionsLink from './../../components/PackageInstructionsLink';
import PizzaTracker from './../../components/PizzaTracker';
import ShippingLabelLink from './../../components/ShippingLabelLink';

import {
    ILineItem,
    IState,
} from './../../businessLogic/sagas/serviceRequest';
import {
    actionStatusReset,
    LoadingState,
} from './../../store/actionStatus';
import { CANCEL_LINE_ITEM, cancelLineItem, updateOnsiteOrderDetails, UPDATE_ONSITE_ORDER_DETAILS } from './../../store/deviceInfo/claimOrder';

import Styles from './lineItem.module.scss';

import { checkIsOnSiteRepairFromTier, getUserTierConfig } from 'src/lib/userTierConfig';
import { logPageAction } from 'src/lib/ApplicationInsights';
import CloseOrderModal from './CloseOrderModal';
import { orderUpdateAlertSelector } from './../../store/deviceInfo/selectors';
import { hideOrderUpdateAlert } from './../../store/deviceInfo';
import { SAVE_TRAVEL_SR_OUTCOME } from './../../store/orderCreation';
import { OrderType } from './../../constants/orderType';
import { userAgentTierSelector } from './../../store/user';
import { ORDER_FROM_ROBO_V2 } from './../../constants/common';
import PartnerCommentModal from './PartnerCommentModal';

interface PropsType {
    claimId: string;
    data: ILineItem;
    isOpen?: boolean;
    serviceRequestNumber: number;
    status: string;
    claimOrderId: string;
    zipCode: string;
    countryCode: string;
}

// This logic should eventually be handled by the services.
// Client would just access a property in the line item such as lineItem.name
export function getLineItemName(lineItem: ILineItem, showWarranty: boolean = false): string {
    const {
        accessoryType,
        componentDeviceInfo,
        deviceInfo,
        repairOffer,
    } = lineItem;

    let name = '';

    if (accessoryType && accessoryType !== 'SN') {
        name = accessoryType;
    } else if (componentDeviceInfo) {
        name = componentDeviceInfo.serialNumberType;
    } else {
        name = deviceInfo.localizedName;
    }

    return showWarranty ? `${repairOffer.name} - ${name}` : name.toUpperCase();
}

export function getShippedSerialNumber(states: IState[]): string {
    const found = states.find((state: IState) => (
        state.details && state.details.shippedSerialNumber
    ));
    return found && found.details ? found.details.shippedSerialNumber : '';
}

export default function LineItem(props: PropsType) {
    return (
        <Collapsible
            colorOnOpen={'white'}
            colorOnClose={'white'}
            chevronColor={'doveGray'}
            header={LineItemHeader(props)}
            className={Styles.lineItem}
            isOpen={props.isOpen}
            tableName={getLineItemName(props.data, true)}
        >
            <LineItemBody {...props} />
        </Collapsible>
    );
}

function LineItemBody(props: PropsType) {   
    const [isCancelConfirmModalOpen, setCancelConfirmModalOpen] = React.useState(false);
    const actionName = `${CANCEL_LINE_ITEM}-${props.data.lineItemId}`;
    const cancelStatus = useSelector((state: any) => state.actionStatus[actionName] || {});

    const { progress } = props.data;
    const { name = '' } = progress;


    const refs: any = { cancelButton: null };
    function setCancelButtonRef(el: HTMLDivElement) {
        if (el) refs.cancelButton = el;
    }

    React.useEffect(() => {
        if (cancelStatus.status === LoadingState.success) {
            setCancelConfirmModalOpen(false);
        }
    }, [cancelStatus.status]);

    return (
        <div>
            <div className={Styles.body}>
                <RenderStatus {...props} />
                {name.toUpperCase() !== 'ININVENTORY' &&
                    <RenderDetails
                        isCancelConfirmModalVisible={isCancelConfirmModalOpen}
                        ref={setCancelButtonRef}
                        setCancelConfirmModalOpen={setCancelConfirmModalOpen}
                        {...props}
                    />
                }
            </div>
            <RenderCancelConfirmModal
                refs={refs}
                showModal={isCancelConfirmModalOpen}
                setOpen={setCancelConfirmModalOpen}
                {...props}
            />
        </div>
    );
}

function LineItemHeader(
    { claimId, data, serviceRequestNumber, status }:
        { claimId: string, data: ILineItem, serviceRequestNumber: number, status: string }
) {
    let header;

    if (typeof data.repairOffer.name !== 'undefined') {
        header = [
            data.repairOffer.name,
            getLineItemName(data),
        ].join(' - ');
    } else {
        header = getLineItemName(data);
    }
    const { progress } = data;
    const { name = '' , notification = '', priority = ''} = progress;
    const orderType = `${notification}${priority}`;

    return (
        <div className={Styles.header}>
            <div className={Styles.order}>
                <div className={Styles.headerText}>{header}</div>
            </div>
            {name.toUpperCase() !== 'ININVENTORY' && OrderType[orderType] !== OrderType.Z4E && OrderType[orderType] !== OrderType.Z4F &&
                <div className={Styles.headerRight}>
                    <ShippingLabelLink
                        claimOrderId={claimId}
                        lineItem={data}
                        serialNumber={data.serialNumber}
                        serviceRequestNumber={serviceRequestNumber}
                        status={status}
                    />
                </div>
            }
        </div>
    );
}

function GetWarrantyMessage({ orderType }: { orderType: string }) {
    switch (OrderType[orderType]) {
        case OrderType.Z1A:
            return RenderWarrantyMessage('warranty_message_Z1A');
        case OrderType.Z1B:
            return RenderWarrantyMessage('warranty_message_Z1B');
        case OrderType.Z1C:
            return RenderWarrantyMessage('warranty_message_Z1C');
        case OrderType.Z1D:
            return RenderWarrantyMessage('warranty_message_Z1C', false);
        case OrderType.Z1E:
            return RenderWarrantyMessage('warranty_message_Z1E');
        case OrderType.Z1F:
            return RenderWarrantyMessage('warranty_message_Z1F');
        case OrderType.Z1H:
            return RenderWarrantyMessage('', false);
        case OrderType.Z2A:
        case OrderType.Z2B:
            return RenderWarrantyMessage('warranty_message_Z2');
        case OrderType.Z3A:
            return RenderWarrantyMessage('warranty_message_Z3A', false);
        case OrderType.Z4E:
            return RenderWarrantyMessage('warranty_message_Z4E');
        case OrderType.Z4F:
            return RenderWarrantyMessage('warranty_message_Z4F', false);
        case OrderType.ININVENTORY:
            return RenderWarrantyMessage('warranty_message_InInventory', false);
        default:
            return RenderWarrantyMessage('warranty_message_default');
    }
}

function GetWarrantyTerms(repair?: boolean) {
    const { t } = useTranslation();
    const warrantyRoute = 'https://support.microsoft.com/en-us/help/4493926/warranties-extended-service-plans-and-terms-conditions-for-your-device';
    const warrantyTerm = repair ? 'warranty_repair_and_replacement_subject' : 'warranty_replacement_subject';

    function onClickLink() {
        logPageAction('Warranty terms link clicked');
    }

    return (
        <div>
            {t(warrantyTerm)} <a onClick={onClickLink} href={warrantyRoute} target="_blank" rel="noreferrer noopener">{t('warranty_terms')}</a>.
        </div>
    );
}

function RenderCancelConfirmModal(props: any) {
    const {
        claimId,
        data,
        refs,
        showModal,
        setOpen,
    } = props;

    const { t } = useTranslation();
    const dispatch = useDispatch();
    const actionName = `${CANCEL_LINE_ITEM}-${data.lineItemId}`;
    const cancelStatus = useSelector((state: any) => state.actionStatus[actionName] || {});

    function onCancelConfirm() {
        logPageAction('Cancel Order confirm button clicked');

        dispatch(cancelLineItem({
            claimOrderId: claimId,
            lineItemId: data.lineItemId,
            serviceNotificationNumber: data.serviceNotificationNumber,
        }));
    }

    function onExit() {
        setOpen(false);
        dispatch(actionStatusReset({ actionName }));
    }

    function onExitKeyDown(e: any) {
        if (e.keyCode === KeyCode.KEY_SPACE || e.keyCode === KeyCode.KEY_RETURN) {
            onExit();
        }
    }

    function RenderCancelButton() {
        if (cancelStatus.status === LoadingState.started) {
            return (
                <Spinner
                    className={Styles.spinner}
                    size={SpinnerSize.medium}
                />
            );
        }

        const cancelling = cancelStatus.status === LoadingState.started;
        const rejected = cancelStatus.status === LoadingState.rejected;

        const buttonClassName = clsx(
            Styles.cancelOrder,
            {
                [Styles.disabled]: cancelling,
                [Styles.rejected]: rejected,
            }
        );

        return (
            <PrimaryButton
                ariaLabel={t('confirm_cancel_order')}
                className={buttonClassName}
                text={t(rejected ? 'try_again' : 'cancel_order')}
                disabled={cancelling}
                onClick={onCancelConfirm}
            />
        );
    }

    // determine focus behavior
    const warningDialogue = React.useRef<HTMLDivElement>(null);
    React.useEffect(() => {
        if (showModal) {
            // focus on warning dialog when opened
            if (warningDialogue && warningDialogue.current) {
                warningDialogue.current.focus();
            }
        } else {
            // focus on cancel button
            if (refs.cancelButton) refs.cancelButton.focus();
        }
    }, [showModal]);

    return !showModal || !showCancelButton(cancelStatus) ? null : (
        <div className={Styles.cancelConfirmationModal}>
            <div className={Styles.warningContainer}>
                <div className={Styles.warningIconContainer}>
                    <Icon className={Styles.warningIcon} iconName="WarningSolid" />
                </div>
                <div
                    aria-label={t('cancel_title')}
                    className={Styles.warningTexts}
                    ref={warningDialogue}
                    role="alert"
                    tabIndex={0}
                >
                    <div className={Styles.cancelTitle}>{t('cancel_title')}</div>
                    <div className={Styles.cancelDescription}>{t('cancel_description')}</div>
                </div>
            </div>
            <div className={Styles.warningActions}>
                <div
                    aria-label="exit cancel confirm modal"
                    className={Styles.warningExit}
                    onClick={onExit}
                    onKeyDown={onExitKeyDown}
                    tabIndex={0}
                >
                    <Icon className={Styles.warningExitIcon} iconName="Cancel" />
                </div>
                <div className={Styles.buttonWrap}>
                    <RenderCancelButton />
                </div>
            </div>
        </div>
    );
}

function RenderDetail({ label, value }: { label: string, value: string }) {
    return value ? (
        <div className={Styles.detail}>
            <div className={Styles.label}>{label}:</div>
            <div className={Styles.value}>{value}</div>
        </div>
    ) : null;
}

const RenderDetails = React.forwardRef((props: any, ref) => {
    const {
        data,
        isCancelConfirmModalVisible,
        setCancelConfirmModalOpen,
        claimOrderId,
        zipCode,
        countryCode,
    } = props;
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const {
        replacementSerialNumber, repairOffer,
        progress = {}, comments, customerComments,
        appointmentDetails = {}, partnerDetails = {},
        serviceRequestId, lineItemId, symptomCode,
        repairDetails, deviceInfo, componentDeviceInfo = {},
        reasonForZ4R, reasonForZ4RDescription,
    } = data;
    const problemDescription = comments === ORDER_FROM_ROBO_V2 ? '' : comments;
    const { isAppointmentConfirmed = false, confirmedAppointmentSchedule } = appointmentDetails;
    const { partnerComments, partnerReferenceNumber } = partnerDetails;
    const { states = [] } = progress;
    const state = states.find((s: any) => s.details) || {};
    const { details = {} } = state;
    const problem = `${data.symptomCode} - ${data.symptomDescription}`;
    const userTierConfig = getUserTierConfig();
    const onsiteOrderActionName = `${UPDATE_ONSITE_ORDER_DETAILS}-${claimOrderId}`;
    const confirmOrderStatus = useSelector((state: any) => state.actionStatus[onsiteOrderActionName] || {});
    const orderUpdateAlertStatus =  useSelector(state => orderUpdateAlertSelector(state));
    const travelSROrderStatus = useSelector((state: any) => state.actionStatus[`${SAVE_TRAVEL_SR_OUTCOME}-${claimOrderId}`] || {});
    const [showCloseOrderModal, setShowCloseOrderModal] = React.useState(false);
    const [closeOrderButtonDisabled, setCloseOrderButtonDisabled] = React.useState(true);
    const userAgentTier = useSelector(state => userAgentTierSelector(state));
    const [partnerFormState, setPartnerFormState] = React.useState(
        {
            partnerComments,
            partnerReferenceNumber,
            confirmedAppointmentSchedule: confirmedAppointmentSchedule ? new Date(Date.parse(confirmedAppointmentSchedule)) : null,
            validation: {
                partnerReferenceNumber: '',
                confirmedAppointmentSchedule: '',
            },
        }
    );

    function handlePartnerForm(event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) {
        const { name } = event.target as HTMLInputElement;
        setPartnerFormState({ ...partnerFormState, [name]: newValue });
        setCloseOrderButtonDisabled(false);
    }

    const handleConfirmedAppointment = (datetime: any) => {
        setPartnerFormState({ ...partnerFormState, confirmedAppointmentSchedule: datetime });
        setCloseOrderButtonDisabled(false);
    };

    const buttonClassName = clsx(
        Styles.cancelOrder,
        {
            [Styles.disabled]: disableCancelButton(),
        }
    );

    function onCancel() {
        logPageAction('Cancel Order button clicked');
        setCancelConfirmModalOpen(true);
    }

    function disableCancelButton() {
        const actionName = `${CANCEL_LINE_ITEM}-${data.lineItemId}`;
        const cancelStatus = useSelector((state: any) => state.actionStatus[actionName] || {});

        return (
            !data.isCancellable ||
            !data.serviceNotificationNumber ||
            !userTierConfig.showCancelOrder ||
            cancelStatus.status === LoadingState.success ||
            data.status === 'cancelpending' ||
            data.status === 'cancelled' ||
            data.status === 'closed' ||
            data.status === 'rejected' ||
            data.status === 'confirmed' ||
            data.status === 'completepending'
        );
    }

    function hideCancelButton() {
        return isCancelConfirmModalVisible;
    }

    function orderCloseModalClose() {
        setShowCloseOrderModal(false);
    }

    const RenderCancelButton = React.forwardRef((props, ref: any) => {
        return hideCancelButton() ? null : (
            <PrimaryButton
                componentRef={ref}
                className={buttonClassName}
                text={t('cancel_order')}
                onClick={onCancel}
                disabled={disableCancelButton()}
            />
        );
    });

    function disablePartnerForm() {
        return (
            !data.isCancellable ||
            !data.serviceNotificationNumber ||
            !userTierConfig.showCancelOrder ||
            travelSROrderStatus.status === LoadingState.success ||
            data.isCompleteSubmitted ||
            data.status === 'cancelpending' ||
            data.status === 'cancelled' ||
            data.status === 'closed' ||
            data.status === 'rejected' ||
            data.status === 'completepending'
        );
    }

    const RenderConfirmButton = (props: any) => {
        if (confirmOrderStatus.status === LoadingState.started) {
            return (
                <Spinner
                    className={Styles.spinner}
                    size={SpinnerSize.medium}
                />
            );
        }

        function isValid() {
            const errors = {
                partnerReferenceNumber: partnerFormState.partnerReferenceNumber.trim() ? '' : t('required_message', { fieldName: t('partner_reference_order_no') }),
                confirmedAppointmentSchedule: partnerFormState.confirmedAppointmentSchedule ? '' : t('required_message', { fieldName: t('confirmed_onsite_appointment') }),
            };
            setPartnerFormState({ ...partnerFormState, validation: errors });
            return !Object.keys(errors).some(key => errors[key]);
        }

        function onConfirm() {
            if(!isValid()) return;

            logPageAction('Confirm Order button clicked');
            dispatch(updateOnsiteOrderDetails({
                claimOrderId,
                lineItemId,
                partnerDetails,
                appointmentDetails,
                serviceRequestId,
                partnerComments: partnerFormState.partnerComments,
                partnerReferenceNumber: partnerFormState.partnerReferenceNumber,
                confirmedAppointmentSchedule: partnerFormState.confirmedAppointmentSchedule,
                ReasonForZ4R: null,
                ReasonForZ4RDescription: '',
            }));
            setCloseOrderButtonDisabled(true);
        }

        
    
        const updating = confirmOrderStatus.status === LoadingState.started;
        const updateFailed = confirmOrderStatus.status === LoadingState.rejected;
        const disableButton: boolean = updating || disablePartnerForm() || closeOrderButtonDisabled;

        if(updateFailed){
            setCloseOrderButtonDisabled(false);
        }

        const confirmButtonClassName = clsx(
            {
                [Styles.disabled]: disableButton,
                [Styles.rejected]: updateFailed,
                [Styles.reConfirmButton]: isAppointmentConfirmed,
                [Styles.confirmButton]: !isAppointmentConfirmed,
            }
        );

        return (
            <PrimaryButton
                className={confirmButtonClassName}
                text={t(updateFailed ? 'try_again' : isAppointmentConfirmed ? 'reconfirm_order': 'confirm_order')}
                onClick={onConfirm}
                disabled={disableButton}
            />
        );
    };

    const RenderCloseOrderButton = (props: any) => {
        function onClose() {
            setShowCloseOrderModal(true);
        }

        return (
            <PrimaryButton
                className={disablePartnerForm() ? Styles.disabled : Styles.closeOrderButton}
                text={t('close_order')}
                onClick={onClose}
                disabled={disablePartnerForm()}
            />
        );
    };

    const { notification = '', priority = '' } = progress;
    const orderType = `${notification}${priority}`;
    return (
        <div className={Styles.details}>
            <div className={Styles.detail}>
                <div className={Styles.label}>{t('service_request_serial_number')}:</div>
                <div className={Styles.value}>{componentDeviceInfo?.serialNumber ? componentDeviceInfo?.serialNumber : deviceInfo?.serialNumber}</div>
            </div>
            <RenderDetail
                label={OrderType[orderType] === OrderType.Z1H ? t('shipped_serial_number') : t('replacement_serial_number')}
                value={replacementSerialNumber || getShippedSerialNumber(states)}
            />
            <div className={Styles.detail}>
                <div className={Styles.label}>{t('service_notification_number')}:</div>
                <div className={Styles.value}>{details.serviceNotificationNumber}</div>
            </div>
            {data.symptomDescription && <div className={Styles.detail}>
                <div className={Styles.label}>{t('problem_with_device')}:</div>
                <div className={Styles.deviceProblem}>{problem}</div>
            </div>}
            <div className={Styles.detail}>
                <div className={Styles.label}>{t('problem_description')}:</div>
                <div className={Styles.deviceProblem}>{problemDescription}</div>
            </div>
            <div className={Styles.detail}>
                <div className={Styles.label}>{t('offer_description')}:</div>
                <div className={OrderType[orderType] !== OrderType.Z4E ? Styles.offerDescription: Styles.deviceProblem}>{repairOffer.description}</div>
            </div>
            {reasonForZ4R && (
                <div className={Styles.detail}>
                    <div className={Styles.label}>{t('z4r_reason')}:</div>
                    <div className={Styles.value}>{reasonForZ4R}</div>
                </div>
            )}
            {reasonForZ4RDescription && (
                <div className={Styles.detail}>
                    <div className={Styles.label}>{t('z4r_reason_description')}:</div>
                    <div className={Styles.value}>{reasonForZ4RDescription}</div>
                </div>
            )}
            {checkIsOnSiteRepairFromTier(userAgentTier) && OrderType[orderType] === OrderType.Z4E &&
                <RenderPartnerForm customerComments={customerComments}
                    handlePartnerForm={handlePartnerForm} partnerFormState={partnerFormState}
                    handleConfirmedAppointment={handleConfirmedAppointment}
                    orderUpdateAlertStatus={orderUpdateAlertStatus}
                    disablePartnerForm={disablePartnerForm}
                />}
            {checkIsOnSiteRepairFromTier(userAgentTier) && OrderType[orderType] === OrderType.Z4E ?
                <div className={Styles.partnerformButtons}>
                    {
                        isAppointmentConfirmed ?
                        <RenderCloseOrderButton /> : <RenderCancelButton ref={ref}/>
                    }
                    <RenderConfirmButton />
                </div> :
                <RenderCancelButton ref={ref} />}
            {
                showCloseOrderModal &&
                <CloseOrderModal
                    orderCloseModalClose={orderCloseModalClose}
                    claimOrderId={claimOrderId}
                    travelSROrderStatus={travelSROrderStatus}
                    symptomCode={symptomCode}
                    zipCode={zipCode}
                    repairDetails={repairDetails}
                    countryCode={countryCode}
                    deviceInfo={deviceInfo} />
            }
        </div>
    );
});

function RenderStatus(props: any) {
    const { data } = props;
    const { t } = useTranslation();
    const { deviceInfo, progress, itemAttributes, partnerDetails={}, appointmentDetails={}, travelOutcome='' } = data;
    const { name = '', states = [], notification = '', priority = '' } = progress;
    let orderType = `${notification}${priority}`;
    const [partnerCommentModalOpen, setPartnerCommentModalOpen] = React.useState(false);

    if (name.toUpperCase() === 'ININVENTORY') {
        orderType = 'ININVENTORY';
    }

    function partnerCommentModalOpenClick(){
        setPartnerCommentModalOpen(true);
    }

    function partnerCommentModalCloseClick(){
        setPartnerCommentModalOpen(false);
    }

    return (
        <div className={Styles.status}>
            <div className={Styles.statusTitle}>{deviceInfo.localizedName}</div>
            <div className={Styles.detail}>
                <div className={Styles.label}>{t('order_type')}:</div>
                <div className={Styles.value}>{t(name)}</div>
            </div>
            <GetWarrantyMessage orderType={orderType} />
            <div className={Styles.pizzaTracker}>
                <PizzaTracker
                    itemAttributes={itemAttributes}
                    progressName={name}
                    states={states}
                    partnerDetails={partnerDetails}
                    appointmentDetails={appointmentDetails}
                    orderType={orderType}
                    travelOutcome={travelOutcome}
                    onPartnerCommentShowMoreClick={partnerCommentModalOpenClick}
                />
            </div>
            {OrderType[orderType] !== OrderType.ININVENTORY && OrderType[orderType] !== OrderType.Z1H && OrderType[orderType] !== OrderType.Z4E &&
                <div className={Styles.detailLink}>
                    <PackageInstructionsLink {...props} />
                </div>
            }
            {partnerCommentModalOpen &&
                <PartnerCommentModal
                    partnerComments={partnerDetails.partnerComments}
                    partnerCommentModalCloseClick={partnerCommentModalCloseClick}/>}
        </div>
    );
}

function RenderWarrantyMessage(message: string, warrantyTerms = true) {
    const { t } = useTranslation();
    return (
        <div className={Styles.orderDescription}>
            <div>
                {t(message)}
            </div>
            {warrantyTerms && GetWarrantyTerms()}
        </div>
    );
}

function showCancelButton(cancelStatus: { status: LoadingState }): boolean {
    if (cancelStatus.status === LoadingState.success) return false;
    return true;
}

function RenderPartnerForm(props: any){
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const iconProps = { iconName: 'Calendar' };
    const { customerComments, handlePartnerForm, partnerFormState,
        handleConfirmedAppointment, orderUpdateAlertStatus, disablePartnerForm } = props;

    React.useEffect(() => {
        if(orderUpdateAlertStatus){
            setTimeout(() => {
                dispatch(hideOrderUpdateAlert());
            }, 3000);
        }
    }, [orderUpdateAlertStatus]);

    return (
            <div className={Styles.marginRightPartnerForm}>
                <div className={Styles.detail}>
                    <div className={Styles.label}>{t('customer_comments')}:</div>
                    <div className={Styles.deviceProblem}>{customerComments}</div>
                </div>
                <div className={Styles.detail}>
                    <div className={Styles.label}>{t('partner_reference_order_no')}:</div>
                    <TextField
                        name="partnerReferenceNumber"
                        onChange={handlePartnerForm}
                        required={true}
                        value={partnerFormState.partnerReferenceNumber}
                        errorMessage={partnerFormState.validation.partnerReferenceNumber}
                        disabled={disablePartnerForm()}
                    />
                </div>
                <div className={Styles.detail}>
                    <div className={Styles.label}>{t('confirmed_onsite_appointment')}:</div>
                    <DatePicker
                        showTimeSelect={true}
                        required={true}
                        onChange={handleConfirmedAppointment}
                        dateFormat="MMMM d, yyyy h:mm aa"
                        selected={partnerFormState.confirmedAppointmentSchedule}
                        disabled={disablePartnerForm()}
                        minDate={new Date()}
                        customInput={
                            <TextField
                                name="confirmedAppointmentSchedule"
                                iconProps={iconProps}
                                disabled={disablePartnerForm()}
                                errorMessage={partnerFormState.validation.confirmedAppointmentSchedule}/>
                        }
                    />
                </div>
                <div className={Styles.detail}>
                    <div className={Styles.label}>{t('partner_comments')}:</div>
                    <TextField
                        name="partnerComments"
                        onChange={handlePartnerForm}
                        value={partnerFormState.partnerComments}
                        multiline={true}
                        disabled={disablePartnerForm()}
                        />
                </div>
                <div className={clsx(Styles.detail, Styles.bottomPaddingOrderDetail)}>
                    {
                        orderUpdateAlertStatus &&
                        <MessageBar
                            messageBarType={MessageBarType.success}
                            isMultiline={false}
                        >
                            {t('nt_confirmed')}
                        </MessageBar>
                    }
                </div>
            </div>
    );
}